import { useCallback, useEffect, useRef, useState } from "react";
import { useWindowResize } from "../../hooks";

export interface Position {
  x: number;
  y: number;
}

export type InitialPosition = Position | "bottom-right" | "bottom-left" | "top-right" | "top-left" | "center";

export interface UseDraggableWindowOptions {
  /** Initial position of the window */
  initialPosition?: InitialPosition;
  /** LocalStorage key for persisting position */
  persistKey?: string;
  /** Whether to constrain window within viewport */
  constrainToViewport?: boolean;
  /** Padding from viewport edges when constrained */
  viewportPadding?: number;
  /** Called when window position changes */
  onPositionChange?: (position: Position) => void;
  /** Window width for position calculations */
  windowWidth?: number;
  /** Window height for position calculations */
  windowHeight?: number;
}

export interface UseDraggableWindowReturn {
  /** Current position of the window */
  position: Position;
  /** Set position manually */
  setPosition: (position: Position | ((prev: Position) => Position)) => void;
  /** Whether the window is currently being dragged */
  isDragging: boolean;
  /** Props to spread on the drag handle element */
  dragHandleProps: {
    onMouseDown: (e: React.MouseEvent) => void;
    style: React.CSSProperties;
  };
  /** Ref to attach to the window container */
  windowRef: React.RefObject<HTMLDivElement | null>;
  /** Reset position to initial value */
  resetPosition: () => void;
}

const STORAGE_KEY_PREFIX = "DraggableWindow-";

/**
 * Loads position from localStorage
 */
function loadPosition(key: string): Position | null {
  if (typeof window === "undefined") return null;

  const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${key}`);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      if (typeof parsed.x === "number" && typeof parsed.y === "number") {
        return parsed;
      }
    } catch {
      // Invalid JSON, return null
    }
  }
  return null;
}

/**
 * Saves position to localStorage
 */
function savePosition(key: string, position: Position): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(`${STORAGE_KEY_PREFIX}${key}`, JSON.stringify(position));
}

/**
 * Calculates initial position based on preset or custom position
 */
function calculateInitialPosition(
  initialPosition: InitialPosition,
  windowWidth: number,
  windowHeight: number,
  padding: number
): Position {
  if (typeof initialPosition === "object") {
    return initialPosition;
  }

  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1024;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 768;

  switch (initialPosition) {
    case "bottom-right":
      return {
        x: viewportWidth - windowWidth - padding,
        y: viewportHeight - windowHeight - padding,
      };
    case "bottom-left":
      return {
        x: padding,
        y: viewportHeight - windowHeight - padding,
      };
    case "top-right":
      return {
        x: viewportWidth - windowWidth - padding,
        y: padding,
      };
    case "top-left":
      return {
        x: padding,
        y: padding,
      };
    case "center":
      return {
        x: (viewportWidth - windowWidth) / 2,
        y: (viewportHeight - windowHeight) / 2,
      };
    default:
      return { x: padding, y: padding };
  }
}

/**
 * Constrains position within viewport bounds
 */
function constrainPosition(position: Position, windowWidth: number, windowHeight: number, padding: number): Position {
  if (typeof window === "undefined") return position;

  const maxX = window.innerWidth - windowWidth - padding;
  const maxY = window.innerHeight - windowHeight - padding;

  return {
    x: Math.max(padding, Math.min(position.x, maxX)),
    y: Math.max(padding, Math.min(position.y, maxY)),
  };
}

/**
 * Hook for managing draggable window position and drag behavior.
 *
 * Uses native mouse events (no external drag library) for maximum control
 * and compatibility. Supports position persistence via localStorage and
 * viewport boundary constraints.
 *
 * @example
 * ```tsx
 * function MyWindow() {
 *   const {
 *     position,
 *     isDragging,
 *     dragHandleProps,
 *     windowRef,
 *   } = useDraggableWindow({
 *     initialPosition: "bottom-right",
 *     persistKey: "my-window",
 *     constrainToViewport: true,
 *   });
 *
 *   return (
 *     <div
 *       ref={windowRef}
 *       style={{ position: "fixed", left: position.x, top: position.y }}
 *     >
 *       <div {...dragHandleProps}>Drag me</div>
 *       <div>Content</div>
 *     </div>
 *   );
 * }
 * ```
 */
export function useDraggableWindow(options: UseDraggableWindowOptions = {}): UseDraggableWindowReturn {
  const {
    initialPosition = "bottom-right",
    persistKey,
    constrainToViewport = true,
    viewportPadding = 10,
    onPositionChange,
    windowWidth = 320,
    windowHeight = 400,
  } = options;

  const windowRef = useRef<HTMLDivElement>(null);
  const dragOffsetRef = useRef<Position>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  // Calculate initial position (from storage or preset)
  const getInitialPosition = useCallback((): Position => {
    // Try to load from storage first
    if (persistKey) {
      const stored = loadPosition(persistKey);
      if (stored) {
        // Validate stored position is still within viewport
        if (constrainToViewport) {
          return constrainPosition(stored, windowWidth, windowHeight, viewportPadding);
        }
        return stored;
      }
    }

    // Fall back to calculated initial position
    return calculateInitialPosition(initialPosition, windowWidth, windowHeight, viewportPadding);
  }, [persistKey, initialPosition, constrainToViewport, windowWidth, windowHeight, viewportPadding]);

  const [position, setPositionInternal] = useState<Position>(getInitialPosition);

  // Wrapper for setPosition that handles persistence and constraints
  const setPosition = useCallback(
    (newPosition: Position | ((prev: Position) => Position)) => {
      setPositionInternal((prev) => {
        const nextPosition = typeof newPosition === "function" ? newPosition(prev) : newPosition;

        // Apply constraints if enabled
        let finalPosition = nextPosition;
        if (constrainToViewport) {
          finalPosition = constrainPosition(nextPosition, windowWidth, windowHeight, viewportPadding);
        }

        // Persist if key provided
        if (persistKey) {
          savePosition(persistKey, finalPosition);
        }

        // Notify callback
        onPositionChange?.(finalPosition);

        return finalPosition;
      });
    },
    [constrainToViewport, windowWidth, windowHeight, viewportPadding, persistKey, onPositionChange]
  );

  // Handle mouse down on drag handle
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Only respond to left click
      if (e.button !== 0) return;

      // Prevent text selection during drag
      e.preventDefault();

      // Calculate offset from mouse position to window position
      dragOffsetRef.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };

      setIsDragging(true);
    },
    [position]
  );

  // Handle mouse move during drag
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      let newPosition: Position = {
        x: e.clientX - dragOffsetRef.current.x,
        y: e.clientY - dragOffsetRef.current.y,
      };

      // Apply constraints
      if (constrainToViewport) {
        newPosition = constrainPosition(newPosition, windowWidth, windowHeight, viewportPadding);
      }

      setPositionInternal(newPosition);
    };

    const handleMouseUp = () => {
      setIsDragging(false);

      // Persist final position
      if (persistKey) {
        setPositionInternal((current) => {
          savePosition(persistKey, current);
          onPositionChange?.(current);
          return current;
        });
      }
    };

    // Add document-level listeners
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    // Prevent selection during drag
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [isDragging, constrainToViewport, windowWidth, windowHeight, viewportPadding, persistKey, onPositionChange]);

  // Handle window resize - re-constrain position
  useWindowResize(() => {
    if (!constrainToViewport) return;

    setPositionInternal((current) => {
      const constrained = constrainPosition(current, windowWidth, windowHeight, viewportPadding);

      // Only update if position changed
      if (constrained.x !== current.x || constrained.y !== current.y) {
        if (persistKey) {
          savePosition(persistKey, constrained);
        }
        onPositionChange?.(constrained);
        return constrained;
      }

      return current;
    });
  });

  // Reset position to initial
  const resetPosition = useCallback(() => {
    const newPosition = calculateInitialPosition(initialPosition, windowWidth, windowHeight, viewportPadding);
    setPosition(newPosition);
  }, [initialPosition, windowWidth, windowHeight, viewportPadding, setPosition]);

  return {
    position,
    setPosition,
    isDragging,
    dragHandleProps: {
      onMouseDown: handleMouseDown,
      style: {
        cursor: isDragging ? "grabbing" : "grab",
      },
    },
    windowRef,
    resetPosition,
  };
}
